// Pages/Assets/WithdrawHistory.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Icon,
  TextField,
  InputAdornment,
  MenuItem,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useDispatch, useSelector } from "react-redux";
import { fetchMyWithdrawals } from "../../store/Slices/withdrawSlice";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

// نفس الأيقونات
import usdtIcon from "../../assets/usdt.png";
import btcIcon from "../../assets/bitcoin.png";
import ltcIcon from "../../assets/litecoin.png";
import xrpIcon from "../../assets/xrp.png";

const COIN_META = {
  USDT: { name: "Tether", icon: usdtIcon },
  BTC: { name: "Bitcoin", icon: btcIcon },
  LTC: { name: "Litecoin", icon: ltcIcon },
  XRP: { name: "XRP", icon: xrpIcon },
};

const fmt = (n, d = 8) => Number(n ?? 0).toFixed(d);
const dt = (v) => new Date(v).toLocaleString();

const StatusChip = ({ status }) => {
  const s = String(status || "").toLowerCase();
  const map = {
    pending: { color: "warning", label: "Pending" },
    approved: { color: "info", label: "Approved" },
    completed: { color: "success", label: "Completed" },
    rejected: { color: "error", label: "Rejected" },
  };
  const conf = map[s] || { color: "default", label: status || "—" };
  return (
    <Chip
      size="small"
      color={conf.color}
      label={conf.label}
      variant="outlined"
    />
  );
};

export default function WithdrawHistory() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { mine = [], listLoading } = useSelector((s) => s.withdraw ?? {});
  const loading = !!listLoading;

  // فلترة/بحث
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  useEffect(() => {
    dispatch(fetchMyWithdrawals());
  }, [dispatch]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (mine || [])
      .filter((r) =>
        statusFilter === "All" ? true : r.status === statusFilter
      )
      .filter((r) => {
        if (!needle) return true;
        const hay = [r.asset_symbol, r.network, r.to_address, r?._id]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(needle);
      });
  }, [mine, q, statusFilter]);

  const [detail, setDetail] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openDetail = (item) => {
    setDetail(item);
    if (isMobile) setDrawerOpen(true);
  };
  const closeDetail = () => {
    setDetail(null);
    setDrawerOpen(false);
  };

  const copy = async (v) => {
    try {
      await navigator.clipboard.writeText(v || "");
    } catch {}
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100dvh" }}>
      {/* TopNav */}
      <AppBar
        position="sticky"
        elevation={0}
        color="transparent"
        sx={{
          bgcolor: "transparent",
          color: "text.primary", // ← نفس الفكرة
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBackIosNewIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1 }} color="inherit">
            Withdrawal History
          </Typography>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                color="inherit"
                onClick={() => dispatch(fetchMyWithdrawals())}
                disabled={loading}
              >
                {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Toolbar (filters) */}
      <Box sx={{ p: { xs: 2, md: 3 }, pt: 2, display: "grid", gap: 1.5 }}>
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent sx={{ display: "grid", gap: 1.2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems="stretch"
            >
              <TextField
                size="small"
                placeholder="Search asset / address / id"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1, minWidth: 240 }}
              />
              <TextField
                size="small"
                select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ width: { xs: "100%", sm: 200 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterListIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              >
                {["All", "Pending", "Approved", "Rejected", "Completed"].map(
                  (s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  )
                )}
              </TextField>
            </Stack>
          </CardContent>
        </Card>

        {/* List */}
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            {!filtered || filtered.length === 0 ? (
              <Alert severity="info">No withdrawals yet.</Alert>
            ) : (
              <List dense disablePadding sx={{ display: "grid", gap: 0.75 }}>
                {filtered.map((it) => (
                  <ListItem
                    key={it._id}
                    onClick={() => openDetail(it)}
                    sx={{
                      px: 1,
                      py: 0.75,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      bgcolor: "background.paper",
                      cursor: "pointer",
                    }}
                    secondaryAction={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <StatusChip status={it.status} />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetail(it);
                          }}
                        >
                          Details
                        </Button>
                      </Stack>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar src={COIN_META[it.asset_symbol]?.icon} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ flexWrap: "wrap" }}
                        >
                          <b>{it.asset_symbol}</b>
                          <span>•</span>
                          <span>{it.network}</span>
                          <span>•</span>
                          <span>
                            Amount: {fmt(it.amount)} {it.asset_symbol}
                          </span>
                          <span>•</span>
                          <span>
                            Net: {fmt(it.net_amount)} {it.asset_symbol}
                          </span>
                        </Stack>
                      }
                      secondary={
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ flexWrap: "wrap", color: "text.secondary" }}
                        >
                          <span>{dt(it.created_at)}</span>
                          <span>•</span>
                          <span>To: {it.to_address}</span>
                        </Stack>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Details — Dialog (desktop) */}
      <Dialog
        open={!!detail && !isMobile}
        onClose={closeDetail}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Withdrawal Details</DialogTitle>
        <DialogContent dividers>
          {detail && <DetailsContent item={detail} onCopy={copy} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Details — Bottom Drawer (mobile) */}
      <Drawer
        anchor="bottom"
        open={drawerOpen && !!detail}
        onClose={closeDetail}
        PaperProps={{
          sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, p: 2 },
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1.25, textAlign: "center" }}>
          Withdrawal Details
        </Typography>
        {detail && <DetailsContent item={detail} onCopy={copy} />}
        <Box sx={{ mt: 1.5, textAlign: "center" }}>
          <Button onClick={closeDetail}>Close</Button>
        </Box>
      </Drawer>
    </Box>
  );
}

function DetailsContent({ item, onCopy }) {
  const Row = ({ label, children }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        my: 0.25,
      }}
    >
      <span style={{ opacity: 0.8 }}>{label}</span>
      <span>{children}</span>
    </Box>
  );
  return (
    <Box sx={{ display: "grid", gap: 1.0 }}>
      <Row label="Status">
        <StatusChip status={item.status} />
      </Row>
      <Row label="Asset">{item.asset_symbol}</Row>
      <Row label="Network">{item.network}</Row>
      <Row label="To">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>{item.to_address}</span>
          <Tooltip title="Copy address">
            <IconButton size="small" onClick={() => onCopy(item.to_address)}>
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Row>
      <Row label="Amount">
        {fmt(item.amount)} {item.asset_symbol}
      </Row>
      <Row label="Price (USDT)">{fmt(item.price_usdt ?? 0, 4)}</Row>
      <Row label="Platform fee">
        {item.asset_symbol === "USDT"
          ? `5.00000000 ${item.asset_symbol}`
          : `${fmt(item.platform_fee_asset)} ${item.asset_symbol} (≈ ${Number(
              item.platform_fee_usdt ?? 5
            ).toFixed(2)} USDT)`}
      </Row>
      <Row label="Network fee">
        {item.asset_symbol === "USDT"
          ? `0.00000000 ${item.asset_symbol}`
          : `${fmt(item.network_fee_asset)} ${item.asset_symbol} (${(
              (item.network_fee_pct ?? 0) * 100
            ).toFixed(2)}%)`}
      </Row>
      <Row label="Total fees">
        {fmt(item.total_fee_asset)} {item.asset_symbol}
      </Row>
      <Row label="Net">
        {fmt(item.net_amount)} {item.asset_symbol}
      </Row>
      <Row label="Created At">{dt(item.created_at)}</Row>
      {item.updated_at ? (
        <Row label="Updated At">{dt(item.updated_at)}</Row>
      ) : null}
      {item.reject_reason ? (
        <Row label="Reason">{item.reject_reason}</Row>
      ) : null}
    </Box>
  );
}
